import { tv } from 'tailwind-variants'

import { typography } from '@gto/ui/typography'

export const inputVariants = tv({
  slots: {
    root: [
      'group box-border w-full transition-colors focus-visible:outline-none flex items-center',

      // Shape and text, which every variant shares — only the FILL differs.
      'rounded-sm text-text-secondary border border-solid border-transparent',
      'focus-within:text-text-primary focus-within:border-primary-main',

      // Invalid state: native :invalid, or `aria-invalid` through the `ui-invalid` marker.
      'has-[:invalid]:border-error-main has-[:invalid]:text-text-primary has-[:invalid]:placeholder:text-text-primary',
      '[&.ui-invalid]:border-error-main [&.ui-invalid]:text-text-primary [&.ui-invalid]:placeholder:text-text-primary',
      'group-data-[invalid]/field:border-error-main',

      // Disabled state
      'has-[:disabled]:border-transparent has-[:disabled]:text-text-disabled',
    ],
    input: [
      'w-full bg-transparent border-none focus-visible:outline-none',
      'placeholder:text-text-secondary',
      'group-hover:text-text-primary',
      'invalid:text-text-primary',
      'group-data-[invalid]/field:text-text-primary',
      'group-has-[:disabled]:text-text-disabled group-has-[:disabled]:placeholder:text-text-disabled',
      'disabled:text-text-disabled disabled:placeholder:text-text-disabled',
    ],
    addon:
      'flex shrink-0 gap-1 cursor-text select-none items-center justify-center px-2 [&_svg]:pointer-events-none data-[slot="start-addon"]:pr-1 data-[slot="end-addon"]:pl-1',
  },
  variants: {
    variant: {
      /** The default: a translucent light fill, for a field on a dark surface. */
      filled: {
        root: [
          'bg-components-input-filled-enabledFill',
          'hover:bg-components-input-filled-hoverFill',
          'focus-within:bg-components-input-filled-enabledFill',
          'has-[:disabled]:bg-components-input-filled-enabledFill',
        ],
      },
      /**
       * For a field on an ELEVATED surface, such as the body of a selected
       * `SelectionCard`. `filled` is a translucent LIGHT fill, so on a light
       * surface it renders lighter still and the field reads as raised rather
       * than as somewhere to type. These fills are opaque, so the field does
       * not take the lightness of its host.
       *
       * Use it on surfaces ABOVE `elevation5` only — the fill is `elevation5`
       * itself, so on anything darker it reads raised, which is the very fault
       * it exists to correct.
       */
      recessed: {
        root: [
          'bg-paper-elevation5',
          'hover:bg-paper-elevation6',
          'focus-within:bg-paper-elevation5',
          'has-[:disabled]:bg-paper-elevation5',
        ],
      },
    },
    size: {
      small: {
        // Height lives on the bordered root, which is `box-border`, so the 1px
        // border is absorbed into the total — keeping the rendered control at
        // 32px, not 34px. The input fills the root via `h-full` so the whole
        // box stays clickable.
        root: 'h-8',
        input: [typography['input/value/small'], 'h-full px-2 py-1'],
        addon: '[&_svg:not([class*="size-"])]:size-5',
      },
      medium: {
        root: 'h-12',
        input: [typography['input/value/medium'], 'h-full p-2'],
        addon: '[&_svg:not([class*="size-"])]:size-5',
      },
    },
    textAlign: {
      left: {
        input: 'text-left',
      },
      center: {
        input: 'text-center',
      },
      right: {
        input: 'text-right',
      },
    },
  },
  defaultVariants: {
    variant: 'filled',
    size: 'small',
    textAlign: 'left',
  },
})
